import React, {useEffect, useState} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

interface PublishedWebsiteFeedbackProps {
  liveUrl: string
  onDismiss: () => void
  title?: string
}

export function PublishedWebsiteCopyError({message, onDismiss}: {message: string; onDismiss: () => void}) {
  return (
    <Alert
      severity="error"
      role="alert"
      sx={{position: 'absolute', left: 1, right: 1, zIndex: 10}}
      action={(
        <Tooltip title="Dismiss copy error">
          <IconButton size="small" color="inherit" aria-label="Dismiss copy error" onClick={onDismiss}>×</IconButton>
        </Tooltip>
      )}
    >
      {message}
    </Alert>
  )
}

export function PublishedWebsiteFeedback({liveUrl, onDismiss, title = 'Website published successfully'}: PublishedWebsiteFeedbackProps) {
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setCopied(false)
    setError(null)
  }, [liveUrl])

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable')
      await navigator.clipboard.writeText(liveUrl)
      setCopied(true)
      setError(null)
    } catch {
      setCopied(false)
      setError('The published link could not be copied. Open it and copy the URL from your browser.')
    }
  }

  return (
    <Alert
      severity="success"
      role="status"
      aria-live="polite"
      sx={{position: 'absolute', top: 1, left: 1, right: 1, zIndex: 9, alignItems: 'center'}}
      action={(
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end'}}>
          <Button size="small" color="inherit" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</Button>
          <Button size="small" color="inherit" component="a" href={liveUrl} target="_blank" rel="noopener noreferrer">Open</Button>
          <Tooltip title="Dismiss published message">
            <IconButton size="small" color="inherit" aria-label="Dismiss published message" onClick={onDismiss}>×</IconButton>
          </Tooltip>
        </Box>
      )}
    >
      <Box sx={{minWidth: 0, maxWidth: '100%'}}>
          <Typography sx={{fontSize: '0.78rem', fontWeight: 700}}>{title}</Typography>
          <Typography
            component="a"
            href={liveUrl}
            title={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{display: 'block', maxWidth: {xs: '45vw', sm: 'min(520px, 55vw)'}, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'inherit', fontSize: '0.72rem'}}
          >
            {liveUrl}
          </Typography>
        {error && <Typography role="alert" sx={{fontSize: '0.72rem', mt: 0.5}}>{error}</Typography>}
      </Box>
    </Alert>
  )
}

/** Owns transient publish/copy notices and clears them when the active session or site changes. */
export function usePublishedWebsiteFeedback(contextKey: string) {
  const [liveUrl, setLiveUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setLiveUrl(null)
    setError(null)
  }, [contextKey])
  return {liveUrl, setLiveUrl, error, setError}
}
